// ── tweaks-panel.jsx ─────────────────────────────────────────

// tweaks-panel.jsx
// Reusable Tweaks shell + form-control helpers.
//
// Owns the host protocol (listens for __activate_edit_mode / __deactivate_edit_mode,
// posts __edit_mode_available / __edit_mode_set_keys / __edit_mode_dismissed) so
// individual prototypes don't re-roll it. Ships a consistent set of controls so you
// don't hand-draw <input type="range">, segmented radios, steppers, etc.
//
// Usage (in an HTML file that loads React + Babel):
//
//   const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
//     "primaryColor": "#D97757",
//     "fontSize": 16,
//     "density": "regular",
//     "dark": false
//   }/*EDITMODE-END*/;
//
//   function App() {
//     const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
//     return (
//       <div style={{ fontSize: t.fontSize, color: t.primaryColor }}>
//         Hello
//         <TweaksPanel>
//           <TweakSection label="Typography" />
//           <TweakSlider label="Font size" value={t.fontSize} min={10} max={32} unit="px"
//                        onChange={(v) => setTweak('fontSize', v)} />
//           <TweakRadio  label="Density" value={t.density}
//                        options={['compact', 'regular', 'comfy']}
//                        onChange={(v) => setTweak('density', v)} />
//           <TweakSection label="Theme" />
//           <TweakColor  label="Primary" value={t.primaryColor}
//                        onChange={(v) => setTweak('primaryColor', v)} />
//           <TweakToggle label="Dark mode" value={t.dark}
//                        onChange={(v) => setTweak('dark', v)} />
//         </TweaksPanel>
//       </div>
//     );
//   }
//
// ─────────────────────────────────────────────────────────────────────────────

const __TWEAKS_STYLE = `
  .twk-panel{position:fixed;right:16px;bottom:16px;z-index:2147483646;width:280px;
    max-height:calc(100vh - 32px);display:flex;flex-direction:column;
    background:rgba(250,249,247,.78);color:#29261b;
    -webkit-backdrop-filter:blur(24px) saturate(160%);backdrop-filter:blur(24px) saturate(160%);
    border:.5px solid rgba(255,255,255,.6);border-radius:14px;
    box-shadow:0 1px 0 rgba(255,255,255,.5) inset,0 12px 40px rgba(0,0,0,.18);
    font:11.5px/1.4 ui-sans-serif,system-ui,-apple-system,sans-serif;overflow:hidden}
  .twk-hd{display:flex;align-items:center;justify-content:space-between;
    padding:10px 8px 10px 14px;cursor:move;user-select:none}
  .twk-hd b{font-size:12px;font-weight:600;letter-spacing:.01em}
  .twk-x{appearance:none;border:0;background:transparent;color:rgba(41,38,27,.55);
    width:22px;height:22px;border-radius:6px;cursor:default;font-size:13px;line-height:1}
  .twk-x:hover{background:rgba(0,0,0,.06);color:#29261b}
  .twk-body{padding:2px 14px 14px;display:flex;flex-direction:column;gap:10px;
    overflow-y:auto;overflow-x:hidden;min-height:0;
    scrollbar-width:thin;scrollbar-color:rgba(0,0,0,.15) transparent}
  .twk-body::-webkit-scrollbar{width:8px}
  .twk-body::-webkit-scrollbar-track{background:transparent;margin:2px}
  .twk-body::-webkit-scrollbar-thumb{background:rgba(0,0,0,.15);border-radius:4px;
    border:2px solid transparent;background-clip:content-box}
  .twk-body::-webkit-scrollbar-thumb:hover{background:rgba(0,0,0,.25);
    border:2px solid transparent;background-clip:content-box}
  .twk-row{display:flex;flex-direction:column;gap:5px}
  .twk-row-h{flex-direction:row;align-items:center;justify-content:space-between;gap:10px}
  .twk-lbl{display:flex;justify-content:space-between;align-items:baseline;
    color:rgba(41,38,27,.72)}
  .twk-lbl>span:first-child{font-weight:500}
  .twk-val{color:rgba(41,38,27,.5);font-variant-numeric:tabular-nums}

  .twk-sect{font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;
    color:rgba(41,38,27,.45);padding:10px 0 0}
  .twk-sect:first-child{padding-top:0}

  .twk-field{appearance:none;width:100%;height:26px;padding:0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;
    background:rgba(255,255,255,.6);color:inherit;font:inherit;outline:none}
  .twk-field:focus{border-color:rgba(0,0,0,.25);background:rgba(255,255,255,.85)}
  select.twk-field{padding-right:22px;
    background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='rgba(0,0,0,.5)' d='M0 0h10L5 6z'/></svg>");
    background-repeat:no-repeat;background-position:right 8px center}

  .twk-slider{appearance:none;-webkit-appearance:none;width:100%;height:4px;margin:6px 0;
    border-radius:999px;background:rgba(0,0,0,.12);outline:none}
  .twk-slider::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;
    width:14px;height:14px;border-radius:50%;background:#fff;
    border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}
  .twk-slider::-moz-range-thumb{width:14px;height:14px;border-radius:50%;
    background:#fff;border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}

  .twk-seg{position:relative;display:flex;padding:2px;border-radius:8px;
    background:rgba(0,0,0,.06);user-select:none}
  .twk-seg-thumb{position:absolute;top:2px;bottom:2px;border-radius:6px;
    background:rgba(255,255,255,.9);box-shadow:0 1px 2px rgba(0,0,0,.12);
    transition:left .15s cubic-bezier(.3,.7,.4,1),width .15s}
  .twk-seg.dragging .twk-seg-thumb{transition:none}
  .twk-seg button{appearance:none;position:relative;z-index:1;flex:1;border:0;
    background:transparent;color:inherit;font:inherit;font-weight:500;height:22px;
    border-radius:6px;cursor:default;padding:0}

  .twk-toggle{position:relative;width:32px;height:18px;border:0;border-radius:999px;
    background:rgba(0,0,0,.15);transition:background .15s;cursor:default;padding:0}
  .twk-toggle[data-on="1"]{background:#34c759}
  .twk-toggle i{position:absolute;top:2px;left:2px;width:14px;height:14px;border-radius:50%;
    background:#fff;box-shadow:0 1px 2px rgba(0,0,0,.25);transition:transform .15s}
  .twk-toggle[data-on="1"] i{transform:translateX(14px)}

  .twk-num{display:flex;align-items:center;height:26px;padding:0 0 0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;background:rgba(255,255,255,.6)}
  .twk-num-lbl{font-weight:500;color:rgba(41,38,27,.6);cursor:ew-resize;
    user-select:none;padding-right:8px}
  .twk-num input{flex:1;min-width:0;height:100%;border:0;background:transparent;
    font:inherit;font-variant-numeric:tabular-nums;text-align:right;padding:0 8px 0 0;
    outline:none;color:inherit;-moz-appearance:textfield}
  .twk-num input::-webkit-inner-spin-button,.twk-num input::-webkit-outer-spin-button{
    -webkit-appearance:none;margin:0}
  .twk-num-unit{padding-right:8px;color:rgba(41,38,27,.45)}

  .twk-btn{appearance:none;height:26px;padding:0 12px;border:0;border-radius:7px;
    background:rgba(0,0,0,.78);color:#fff;font:inherit;font-weight:500;cursor:default}
  .twk-btn:hover{background:rgba(0,0,0,.88)}
  .twk-btn.secondary{background:rgba(0,0,0,.06);color:inherit}
  .twk-btn.secondary:hover{background:rgba(0,0,0,.1)}

  .twk-swatch{appearance:none;-webkit-appearance:none;width:56px;height:22px;
    border:.5px solid rgba(0,0,0,.1);border-radius:6px;padding:0;cursor:default;
    background:transparent;flex-shrink:0}
  .twk-swatch::-webkit-color-swatch-wrapper{padding:0}
  .twk-swatch::-webkit-color-swatch{border:0;border-radius:5.5px}
  .twk-swatch::-moz-color-swatch{border:0;border-radius:5.5px}
`;

// ── useTweaks ───────────────────────────────────────────────────────────────
// Single source of truth for tweak values. setTweak persists via the host
// (__edit_mode_set_keys → host rewrites the EDITMODE block on disk).
function useTweaks(defaults) {
  const [values, setValues] = React.useState(defaults);
  const setTweak = React.useCallback((key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    window.parent.postMessage({ type: '__edit_mode_set_keys', edits: { [key]: val } }, '*');
  }, []);
  return [values, setTweak];
}

// ── TweaksPanel ─────────────────────────────────────────────────────────────
// Floating shell. Registers the protocol listener BEFORE announcing
// availability — if the announce ran first, the host's activate could land
// before our handler exists and the toolbar toggle would silently no-op.
// The close button posts __edit_mode_dismissed so the host's toolbar toggle
// flips off in lockstep; the host echoes __deactivate_edit_mode back which
// is what actually hides the panel.
function TweaksPanel({ title = 'Tweaks', children }) {
  const [open, setOpen] = React.useState(false);
  const dragRef = React.useRef(null);
  const offsetRef = React.useRef({ x: 16, y: 16 });
  const PAD = 16;

  const clampToViewport = React.useCallback(() => {
    const panel = dragRef.current;
    if (!panel) return;
    const w = panel.offsetWidth, h = panel.offsetHeight;
    const maxRight = Math.max(PAD, window.innerWidth - w - PAD);
    const maxBottom = Math.max(PAD, window.innerHeight - h - PAD);
    offsetRef.current = {
      x: Math.min(maxRight, Math.max(PAD, offsetRef.current.x)),
      y: Math.min(maxBottom, Math.max(PAD, offsetRef.current.y)),
    };
    panel.style.right = offsetRef.current.x + 'px';
    panel.style.bottom = offsetRef.current.y + 'px';
  }, []);

  React.useEffect(() => {
    if (!open) return;
    clampToViewport();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', clampToViewport);
      return () => window.removeEventListener('resize', clampToViewport);
    }
    const ro = new ResizeObserver(clampToViewport);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, [open, clampToViewport]);

  React.useEffect(() => {
    const onMsg = (e) => {
      const t = e?.data?.type;
      if (t === '__activate_edit_mode') setOpen(true);
      else if (t === '__deactivate_edit_mode') setOpen(false);
    };
    window.addEventListener('message', onMsg);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const dismiss = () => {
    setOpen(false);
    window.parent.postMessage({ type: '__edit_mode_dismissed' }, '*');
  };

  const onDragStart = (e) => {
    const panel = dragRef.current;
    if (!panel) return;
    const r = panel.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY;
    const startRight = window.innerWidth - r.right;
    const startBottom = window.innerHeight - r.bottom;
    const move = (ev) => {
      offsetRef.current = {
        x: startRight - (ev.clientX - sx),
        y: startBottom - (ev.clientY - sy),
      };
      clampToViewport();
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  if (!open) return null;
  return (
    <>
      <style>{__TWEAKS_STYLE}</style>
      <div ref={dragRef} className="twk-panel"
           style={{ right: offsetRef.current.x, bottom: offsetRef.current.y }}>
        <div className="twk-hd" onMouseDown={onDragStart}>
          <b>{title}</b>
          <button className="twk-x" aria-label="Close tweaks"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={dismiss}>✕</button>
        </div>
        <div className="twk-body">{children}</div>
      </div>
    </>
  );
}

// ── Layout helpers ──────────────────────────────────────────────────────────

function TweakSection({ label, children }) {
  return (
    <>
      <div className="twk-sect">{label}</div>
      {children}
    </>
  );
}

function TweakRow({ label, value, children, inline = false }) {
  return (
    <div className={inline ? 'twk-row twk-row-h' : 'twk-row'}>
      <div className="twk-lbl">
        <span>{label}</span>
        {value != null && <span className="twk-val">{value}</span>}
      </div>
      {children}
    </div>
  );
}

// ── Controls ────────────────────────────────────────────────────────────────

function TweakSlider({ label, value, min = 0, max = 100, step = 1, unit = '', onChange }) {
  return (
    <TweakRow label={label} value={`${value}${unit}`}>
      <input type="range" className="twk-slider" min={min} max={max} step={step}
             value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </TweakRow>
  );
}

function TweakToggle({ label, value, onChange }) {
  return (
    <div className="twk-row twk-row-h">
      <div className="twk-lbl"><span>{label}</span></div>
      <button type="button" className="twk-toggle" data-on={value ? '1' : '0'}
              role="switch" aria-checked={!!value}
              onClick={() => onChange(!value)}><i /></button>
    </div>
  );
}

function TweakRadio({ label, value, options, onChange }) {
  const trackRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);
  const opts = options.map((o) => (typeof o === 'object' ? o : { value: o, label: o }));
  const idx = Math.max(0, opts.findIndex((o) => o.value === value));
  const n = opts.length;

  // The active value is read by pointer-move handlers attached for the lifetime
  // of a drag — ref it so a stale closure doesn't fire onChange for every move.
  const valueRef = React.useRef(value);
  valueRef.current = value;

  const segAt = (clientX) => {
    const r = trackRef.current.getBoundingClientRect();
    const inner = r.width - 4;
    const i = Math.floor(((clientX - r.left - 2) / inner) * n);
    return opts[Math.max(0, Math.min(n - 1, i))].value;
  };

  const onPointerDown = (e) => {
    setDragging(true);
    const v0 = segAt(e.clientX);
    if (v0 !== valueRef.current) onChange(v0);
    const move = (ev) => {
      if (!trackRef.current) return;
      const v = segAt(ev.clientX);
      if (v !== valueRef.current) onChange(v);
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <TweakRow label={label}>
      <div ref={trackRef} role="radiogroup" onPointerDown={onPointerDown}
           className={dragging ? 'twk-seg dragging' : 'twk-seg'}>
        <div className="twk-seg-thumb"
             style={{ left: `calc(2px + ${idx} * (100% - 4px) / ${n})`,
                      width: `calc((100% - 4px) / ${n})` }} />
        {opts.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={o.value === value}>
            {o.label}
          </button>
        ))}
      </div>
    </TweakRow>
  );
}

function TweakSelect({ label, value, options, onChange }) {
  return (
    <TweakRow label={label}>
      <select className="twk-field" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => {
          const v = typeof o === 'object' ? o.value : o;
          const l = typeof o === 'object' ? o.label : o;
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </TweakRow>
  );
}

function TweakText({ label, value, placeholder, onChange }) {
  return (
    <TweakRow label={label}>
      <input className="twk-field" type="text" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} />
    </TweakRow>
  );
}

function TweakNumber({ label, value, min, max, step = 1, unit = '', onChange }) {
  const clamp = (n) => {
    if (min != null && n < min) return min;
    if (max != null && n > max) return max;
    return n;
  };
  const startRef = React.useRef({ x: 0, val: 0 });
  const onScrubStart = (e) => {
    e.preventDefault();
    startRef.current = { x: e.clientX, val: value };
    const decimals = (String(step).split('.')[1] || '').length;
    const move = (ev) => {
      const dx = ev.clientX - startRef.current.x;
      const raw = startRef.current.val + dx * step;
      const snapped = Math.round(raw / step) * step;
      onChange(clamp(Number(snapped.toFixed(decimals))));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return (
    <div className="twk-num">
      <span className="twk-num-lbl" onPointerDown={onScrubStart}>{label}</span>
      <input type="number" value={value} min={min} max={max} step={step}
             onChange={(e) => onChange(clamp(Number(e.target.value)))} />
      {unit && <span className="twk-num-unit">{unit}</span>}
    </div>
  );
}

function TweakColor({ label, value, onChange }) {
  return (
    <div className="twk-row twk-row-h">
      <div className="twk-lbl"><span>{label}</span></div>
      <input type="color" className="twk-swatch" value={value}
             onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function TweakButton({ label, onClick, secondary = false }) {
  return (
    <button type="button" className={secondary ? 'twk-btn secondary' : 'twk-btn'}
            onClick={onClick}>{label}</button>
  );
}

Object.assign(window, {
  useTweaks, TweaksPanel, TweakSection, TweakRow,
  TweakSlider, TweakToggle, TweakRadio, TweakSelect,
  TweakText, TweakNumber, TweakColor, TweakButton,
});


// ── mixer.jsx ─────────────────────────────────────────
// mixer.jsx — color math + mixing engine

// ── Color conversions ──────────────────────────────────────────────────────
const clamp01 = (x) => Math.max(0, Math.min(1, x));
const clamp255 = (x) => Math.max(0, Math.min(255, Math.round(x)));

function hexToRgb(hex) {
  if (!hex) return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}
function rgbToHex({ r, g, b }) {
  const h = (n) => clamp255(n).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`.toUpperCase();
}

function srgbToLinear(c) {
  const x = c / 255;
  return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
}
function linearToSrgb(x) {
  const v = x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055;
  return clamp255(v * 255);
}
function rgbToLinear({ r, g, b }) {
  return { r: srgbToLinear(r), g: srgbToLinear(g), b: srgbToLinear(b) };
}
function linearToRgb({ r, g, b }) {
  return { r: linearToSrgb(r), g: linearToSrgb(g), b: linearToSrgb(b) };
}

// ── Kubelka–Munk (3-channel) ───────────────────────────────────────────────
// Treats each paint's linear-light RGB as reflectance at three notional
// wavelengths. Pigments mix linearly in K/S = (1-R)² / (2R) under the
// Kubelka–Munk opaque-layer model, which is a physically reasonable
// approximation of subtractive paint mixing — yellow+blue → green-ish,
// cyan+magenta → blue-ish, etc. RGB-linear averaging, by contrast, would
// produce grey in both of those cases.
// Reflectance clamp: real pigments never absorb 100% of a band and never
// reflect 100% either. Treating a channel value of 0 or 1 as ideal makes
// K/S blow up (or vanish), which in turn makes white barely lighten a
// pigment and makes pure-primary mixes crash to black. 0.03 is a
// conservative "realistic residual reflectance" — it lets white actually
// tint and keeps mixes in sensible territory.
const R_FLOOR = 0.03;
const R_CEIL  = 1 - R_FLOOR;
function reflectanceToKS(R) {
  const r = Math.max(R_FLOOR, Math.min(R_CEIL, R));
  return (1 - r) * (1 - r) / (2 * r);
}
function ksToReflectance(k) {
  const K = Math.max(0, k);
  return 1 + K - Math.sqrt(K * K + 2 * K);
}
// Input: linear-light RGB 0..1. Output: {r,g,b} of K/S values.
function linearRgbToKS({ r, g, b }) {
  return { r: reflectanceToKS(r), g: reflectanceToKS(g), b: reflectanceToKS(b) };
}
// Input: {r,g,b} of K/S. Output: linear-light RGB 0..1.
function ksToLinearRgb({ r, g, b }) {
  return { r: ksToReflectance(r), g: ksToReflectance(g), b: ksToReflectance(b) };
}
// sRGB byte (0..255) → K/S triplet, going through linear light.
function srgbToKS({ r, g, b }) {
  return linearRgbToKS({
    r: srgbToLinear(r), g: srgbToLinear(g), b: srgbToLinear(b),
  });
}
// K/S triplet → sRGB byte (0..255), going through linear light.
function ksToSrgb(ks) {
  const lin = ksToLinearRgb(ks);
  return {
    r: linearToSrgb(lin.r),
    g: linearToSrgb(lin.g),
    b: linearToSrgb(lin.b),
  };
}

function rgbToXyz({ r, g, b }) {
  const R = srgbToLinear(r), G = srgbToLinear(g), B = srgbToLinear(b);
  return {
    x: (R * 0.4124564 + G * 0.3575761 + B * 0.1804375) * 100,
    y: (R * 0.2126729 + G * 0.7151522 + B * 0.0721750) * 100,
    z: (R * 0.0193339 + G * 0.1191920 + B * 0.9503041) * 100,
  };
}
function xyzToLab({ x, y, z }) {
  const Xn = 95.047, Yn = 100.0, Zn = 108.883;
  const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const fx = f(x / Xn), fy = f(y / Yn), fz = f(z / Zn);
  return { L: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
}
function rgbToLab(rgb) { return xyzToLab(rgbToXyz(rgb)); }
function deltaE76(a, b) {
  const la = rgbToLab(a), lb = rgbToLab(b);
  const dL = la.L - lb.L, da = la.a - lb.a, db = la.b - lb.b;
  return Math.sqrt(dL * dL + da * da + db * db);
}
function deltaELabel(dE) {
  if (dE < 1.5) return { label: 'Excellent', tone: 'ok' };
  if (dE < 3.5) return { label: 'Good', tone: 'ok' };
  if (dE < 7)   return { label: 'Close', tone: 'warn' };
  return { label: 'Off', tone: 'bad' };
}

// ── Simplex projection (Duchi et al. 2008) ─────────────────────────────────
function projectSimplex(v) {
  const n = v.length;
  const u = [...v].sort((a, b) => b - a);
  let cssv = 0, rho = -1;
  for (let i = 0; i < n; i++) {
    cssv += u[i];
    const t = (cssv - 1) / (i + 1);
    if (u[i] - t > 0) rho = i;
  }
  cssv = 0;
  for (let i = 0; i <= rho; i++) cssv += u[i];
  const theta = (cssv - 1) / (rho + 1);
  return v.map((x) => Math.max(0, x - theta));
}

// ── Core optimizer: projected gradient with Armijo line-search ─────────────
// Minimize ||Aw - t||² subject to w on the probability simplex.
// A is a 3×n matrix of paint colors (in linear-light sRGB), t is the target.
function loss(A, w, t) {
  let r0 = -t[0], r1 = -t[1], r2 = -t[2];
  for (let i = 0; i < w.length; i++) {
    r0 += w[i] * A[0][i];
    r1 += w[i] * A[1][i];
    r2 += w[i] * A[2][i];
  }
  return r0 * r0 + r1 * r1 + r2 * r2;
}
function solveFromInit(A, t, w0, iters = 600) {
  const n = w0.length;
  let w = [...w0];
  let step = 0.4;
  for (let it = 0; it < iters; it++) {
    // gradient
    let r0 = -t[0], r1 = -t[1], r2 = -t[2];
    for (let i = 0; i < n; i++) {
      r0 += w[i] * A[0][i];
      r1 += w[i] * A[1][i];
      r2 += w[i] * A[2][i];
    }
    const grad = new Array(n);
    for (let i = 0; i < n; i++) {
      grad[i] = 2 * (A[0][i] * r0 + A[1][i] * r1 + A[2][i] * r2);
    }
    // Armijo line-search: shrink step until loss decreases
    const L0 = r0 * r0 + r1 * r1 + r2 * r2;
    let s = step;
    let wNew = w;
    let accepted = false;
    for (let tryN = 0; tryN < 20; tryN++) {
      const cand = new Array(n);
      for (let i = 0; i < n; i++) cand[i] = w[i] - s * grad[i];
      const proj = projectSimplex(cand);
      const Lnew = loss(A, proj, t);
      if (Lnew < L0 - 1e-10) {
        wNew = proj;
        step = Math.min(1.0, s * 1.2);
        accepted = true;
        break;
      }
      s *= 0.5;
    }
    if (!accepted) break;
    w = wNew;
  }
  return w;
}

// ── Public API: mixPaints ──────────────────────────────────────────────────
function mixPaints(targetHex, paints, opts = {}) {
  const { mode = 'pigment', maxComponents = 4 } = opts;
  const active = paints.filter((p) => p.active !== false);
  if (active.length === 0) return null;
  const tRgb = hexToRgb(targetHex);
  if (!tRgb) return null;
  const pRgb = active.map((p) => hexToRgb(p.hex));

  // Work in K/S space: each paint is represented by its per-channel K/S
  // triplet (computed from linear-light reflectance). Subtractive mixing
  // is linear in K/S, so the existing projected-gradient solver on the
  // simplex still applies — the A matrix just holds K/S values instead of
  // raw sRGB bytes, and the mixed reflectance is recovered via the inverse
  // K/S → R formula at prediction time.
  const pKS = pRgb.map((rgb) => srgbToKS(rgb));
  const tKS = srgbToKS(tRgb);

  // Pigment-aware mode: prefer recipes that avoid metallics/transparents
  // (they add reflection/body, not pure color). We implement this as a
  // preference during subset search, not a hard filter, so the solver can
  // still use them if nothing else works.
  let searchIdxMap = active.map((_, i) => i);
  if (mode === 'pigment') {
    const bodyIdxs = [];
    for (let i = 0; i < active.length; i++) {
      const t = active[i].type;
      if (t !== 'transparent' && t !== 'metallic') bodyIdxs.push(i);
    }
    if (bodyIdxs.length >= 2) searchIdxMap = bodyIdxs;
  }

  // Given weights on a subset of paints, compute the predicted sRGB color
  // via K/S → reflectance → linear RGB → sRGB.
  const predictFromWeights = (w, subKS) => {
    const n = w.length;
    let ksR = 0, ksG = 0, ksB = 0;
    for (let j = 0; j < n; j++) {
      ksR += w[j] * subKS[j].r;
      ksG += w[j] * subKS[j].g;
      ksB += w[j] * subKS[j].b;
    }
    const lin = ksToLinearRgb({ r: ksR, g: ksG, b: ksB });
    return {
      r: linearToSrgb(lin.r),
      g: linearToSrgb(lin.g),
      b: linearToSrgb(lin.b),
    };
  };

  // Exhaustive subset search: for small palettes (≤ ~14 paints), enumerate
  // every combination of size 1..maxComponents, solve the constrained
  // weights on each subset, and keep the one with lowest ΔE. For 12 paints,
  // k=4: C(12,4)=495 subsets — each one cheap. This is deterministic and
  // guaranteed to find the best recipe (unlike greedy which can lock in on
  // the wrong first paint). Falls back to greedy for larger palettes.
  const solveSubset = (idxs) => {
    const n = idxs.length;
    const subKS = idxs.map((i) => pKS[i]);
    // A columns are per-paint K/S triplets; solver fits A·w ≈ t_KS on simplex.
    const A = [
      subKS.map((p) => p.r),
      subKS.map((p) => p.g),
      subKS.map((p) => p.b),
    ];
    const t = [tKS.r, tKS.g, tKS.b];
    const starts = [];
    starts.push(new Array(n).fill(1 / n));
    // Per-vertex starts
    for (let k = 0; k < n; k++) {
      const w = new Array(n).fill(0.02);
      w[k] = 1 - 0.02 * (n - 1);
      starts.push(w);
    }
    // Random Dirichlet — a few extra starts to escape local minima from the
    // deterministic vertex/uniform starts above.
    for (let k = 0; k < 6; k++) {
      const raw = new Array(n).fill(0).map(() => -Math.log(Math.random() + 1e-9));
      const s = raw.reduce((a, b) => a + b, 0);
      starts.push(raw.map((x) => x / s));
    }

    let bestW = null;
    let bestDE = Infinity;
    for (const s0 of starts) {
      const w = solveFromInit(A, t, projectSimplex(s0), 200);
      const predRgb = predictFromWeights(w, subKS);
      const dE = deltaE76(tRgb, predRgb);
      if (dE < bestDE) { bestDE = dE; bestW = w; }
    }
    return { wSub: bestW, dE: bestDE };
  };

  const pool = searchIdxMap.slice();
  let bestFinalWeights = null;
  let bestFinalActive = null;
  let bestFinalDE = Infinity;

  const tryCombo = (idxs) => {
    const { wSub, dE } = solveSubset(idxs);
    // Parsimony: prefer smaller recipes when close in ΔE
    const score = dE + 0.25 * idxs.length;
    if (score < bestFinalDE) {
      bestFinalDE = score;
      bestFinalActive = [...idxs];
      bestFinalWeights = wSub;
    }
  };

  const useExhaustive = pool.length <= 14;
  if (useExhaustive) {
    const maxK = Math.min(maxComponents, pool.length);
    // Enumerate all subsets of size 1..maxK
    const combine = (start, current, k) => {
      if (current.length === k) { tryCombo(current); return; }
      for (let i = start; i < pool.length; i++) {
        current.push(pool[i]);
        combine(i + 1, current, k);
        current.pop();
      }
    };
    for (let k = 1; k <= maxK; k++) combine(0, [], k);
  } else {
    // Greedy fallback for larger palettes
    for (let startK = 0; startK < Math.min(6, pool.length); startK++) {
      let chosen = [pool[startK]];
      tryCombo(chosen);
      while (chosen.length < maxComponents) {
        let bestI = -1, bestDE = Infinity;
        for (const i of pool) {
          if (chosen.includes(i)) continue;
          const { dE } = solveSubset([...chosen, i]);
          if (dE < bestDE) { bestDE = dE; bestI = i; }
        }
        if (bestI < 0) break;
        chosen.push(bestI);
        tryCombo(chosen);
      }
    }
  }

  const final = new Array(active.length).fill(0);
  if (bestFinalActive) {
    for (let k = 0; k < bestFinalActive.length; k++) {
      final[bestFinalActive[k]] = bestFinalWeights[k];
    }
  }

  // Predicted: weighted sum of K/S, then invert to reflectance → sRGB.
  const predRgb = predictFromWeights(final, pKS);
  const predHex = rgbToHex(predRgb);
  const dE = deltaE76(tRgb, predRgb);

  const components = active
    .map((p, i) => ({ paint: p, weight: final[i] }))
    .filter((c) => c.weight > 0.005)
    .sort((a, b) => b.weight - a.weight);

  return { components, predHex, predRgb, dE, target: tRgb };
}

// ── Ratio formatting ───────────────────────────────────────────────────────
function weightsToParts(weights, maxTotal = 20) {
  if (!weights.length) return [];
  const minW = Math.min(...weights.filter((w) => w > 0));
  if (!isFinite(minW) || minW === 0) return weights.map(() => 1);
  const scaled = weights.map((w) => w / minW);
  let best = scaled.map((x) => Math.max(1, Math.round(x)));
  for (let m = 1; m <= 6; m++) {
    const parts = scaled.map((x) => Math.max(1, Math.round(x * m)));
    const sum = parts.reduce((a, b) => a + b, 0);
    if (sum <= maxTotal) { best = parts; break; }
  }
  return best;
}
function gcdMany(arr) {
  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
  return arr.reduce((a, b) => gcd(a, b));
}
function simplifyParts(parts) {
  const g = gcdMany(parts);
  return g > 1 ? parts.map((p) => p / Math.max(1, g)) : parts;
}

Object.assign(window, {
  hexToRgb, rgbToHex, rgbToLinear, linearToRgb,
  rgbToLab, deltaE76, deltaELabel, clamp01, clamp255,
  mixPaints, weightsToParts, simplifyParts,
});


// ── seed.jsx ─────────────────────────────────────────
// seed.jsx — starter palette (modeler/airbrush kit) + types
// Hex values are estimates for a typical hobby acrylic range.

const PAINT_TYPES = [
  { value: 'primary',     label: 'Primary' },
  { value: 'earth',       label: 'Earth' },
  { value: 'white',       label: 'White' },
  { value: 'black',       label: 'Black' },
  { value: 'metallic',    label: 'Metallic' },
  { value: 'transparent', label: 'Transparent' },
];

const SEED_PAINTS = [
  { id: 'p01', name: 'Titanium White',   hex: '#F4F1E8', type: 'white',    notes: 'Opaque base — warms slightly', active: true },
  { id: 'p02', name: 'Carbon Black',     hex: '#1A1A1C', type: 'black',    notes: 'Neutral, slight blue', active: true },
  { id: 'p03', name: 'Process Cyan',     hex: '#0098C6', type: 'primary',  notes: 'Clean cool blue', active: true },
  { id: 'p04', name: 'Magenta',          hex: '#C4246C', type: 'primary',  notes: '', active: true },
  { id: 'p05', name: 'Process Yellow',   hex: '#F2C50A', type: 'primary',  notes: '', active: true },
  { id: 'p06', name: 'Ultramarine Blue', hex: '#2238A0', type: 'primary',  notes: 'Warm blue — good for shadows', active: true },
  { id: 'p07', name: 'Cadmium Red',      hex: '#B52A1F', type: 'primary',  notes: '', active: true },
  { id: 'p08', name: 'Burnt Sienna',     hex: '#8A4A2C', type: 'earth',    notes: 'Great for flesh, rust', active: true },
  { id: 'p09', name: 'Raw Umber',        hex: '#4D3A22', type: 'earth',    notes: 'Cool brown, dark mixes', active: true },
  { id: 'p10', name: 'Yellow Ochre',     hex: '#C89A3E', type: 'earth',    notes: '', active: true },
  { id: 'p11', name: 'Flesh Wash',       hex: '#B88672', type: 'earth',    notes: 'Base for skin tones', active: false },
  { id: 'p12', name: 'Steel',            hex: '#8E9094', type: 'metallic', notes: 'Slightly cool gunmetal', active: false },
];

Object.assign(window, { PAINT_TYPES, SEED_PAINTS });


// ── components.jsx ─────────────────────────────────────────
// components.jsx — reusable UI bits for PaintMix

// Contrast-aware text color for a swatch
function readableOn(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#000';
  const { r, g, b } = rgbToLinear(rgb);
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return L > 0.4 ? 'rgba(30,27,22,.92)' : 'rgba(246,242,234,.95)';
}

// A big color swatch with optional label
function Swatch({ hex, label, sub, size = 96, style = {} }) {
  return (
    <div
      className="swatch"
      style={{
        background: hex,
        width: size,
        height: size,
        color: readableOn(hex),
        ...style,
      }}
    >
      {label && <div className="swatch-label">{label}</div>}
      {sub && <div className="swatch-sub">{sub}</div>}
    </div>
  );
}

// Small dot swatch
function Dot({ hex, size = 14, style = {} }) {
  return (
    <span
      className="dot"
      style={{
        display: 'inline-block',
        width: size, height: size, borderRadius: '50%',
        background: hex,
        boxShadow: 'inset 0 0 0 .5px rgba(0,0,0,.2)',
        ...style,
      }}
    />
  );
}

// Formatted HEX/RGB under a swatch
function ColorReadout({ hex }) {
  const rgb = hexToRgb(hex);
  return (
    <div className="readout">
      <span className="mono">{hex.toUpperCase()}</span>
      {rgb && (
        <span className="mono dim">
          &nbsp;· rgb({rgb.r}, {rgb.g}, {rgb.b})
        </span>
      )}
    </div>
  );
}

// ΔE badge
function DeltaBadge({ dE }) {
  const { label, tone } = deltaELabel(dE);
  return (
    <span className={`delta delta-${tone}`}>
      <span className="mono">ΔE {dE.toFixed(1)}</span>
      <span className="delta-label">{label}</span>
    </span>
  );
}

// Segmented control (lightweight, not from Tweaks)
function Segmented({ value, options, onChange }) {
  return (
    <div className="seg">
      {options.map((o) => {
        const v = typeof o === 'object' ? o.value : o;
        const l = typeof o === 'object' ? o.label : o;
        return (
          <button
            key={v}
            type="button"
            className={v === value ? 'seg-btn on' : 'seg-btn'}
            onClick={() => onChange(v)}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
}

// Icon button (no svg libs — hand-written minimalist glyphs)
function IconButton({ title, onClick, children, tone = 'ghost' }) {
  return (
    <button
      type="button"
      className={`icon-btn icon-${tone}`}
      title={title}
      aria-label={title}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// A pill chip for paint type
function TypeChip({ type }) {
  const meta = PAINT_TYPES.find((t) => t.value === type);
  return <span className={`chip chip-${type}`}>{meta ? meta.label : type}</span>;
}

// Confirm-on-second-click button
function DeleteButton({ onConfirm }) {
  const [armed, setArmed] = React.useState(false);
  React.useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 2200);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button
      type="button"
      className={armed ? 'icon-btn icon-danger armed' : 'icon-btn icon-ghost'}
      onClick={() => (armed ? onConfirm() : setArmed(true))}
      title={armed ? 'Click again to confirm' : 'Delete'}
    >
      {armed ? 'Delete?' : '×'}
    </button>
  );
}

Object.assign(window, {
  readableOn, Swatch, Dot, ColorReadout, DeltaBadge,
  Segmented, IconButton, TypeChip, DeleteButton,
});


// ── library.jsx ─────────────────────────────────────────
// library.jsx — Paint library panel (CRUD)

function PaintLibrary({ paints, setPaints, selectedIds, onToggleSelect, onPickColor }) {
  const [adding, setAdding] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [filter, setFilter] = React.useState('all');

  const filtered = paints.filter((p) => {
    if (filter === 'active') return p.active !== false;
    if (filter === 'inactive') return p.active === false;
    if (filter !== 'all') return p.type === filter;
    return true;
  });

  const updatePaint = (id, patch) =>
    setPaints((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const deletePaint = (id) => setPaints((ps) => ps.filter((p) => p.id !== id));
  const addPaint = (p) => {
    const id = 'p' + Math.random().toString(36).slice(2, 8);
    setPaints((ps) => [...ps, { id, active: true, ...p }]);
    setAdding(false);
  };

  return (
    <div className="lib">
      <div className="lib-head">
        <div>
<h2 className="panel-title">Your paints</h2>
        </div>
        <button className="btn btn-primary" onClick={() => { setAdding(true); setEditingId(null); }}>
          + Add paint
        </button>
      </div>

      <div className="lib-filters">
        <Segmented
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: `All · ${paints.length}` },
            { value: 'active', label: `Active · ${paints.filter((p) => p.active !== false).length}` },
            { value: 'primary', label: 'Primary' },
            { value: 'earth', label: 'Earth' },
            { value: 'white', label: 'W' },
            { value: 'black', label: 'K' },
            { value: 'metallic', label: 'Metal' },
          ]}
        />
      </div>

      {adding && (
        <PaintEditor
          initial={{ name: '', hex: '#888888', type: 'primary', notes: '', active: true }}
          onSave={addPaint}
          onCancel={() => setAdding(false)}
        />
      )}

      <div className="lib-list">
        {filtered.map((p) => (
          <PaintRow
            key={p.id}
            paint={p}
            selected={selectedIds.has(p.id)}
            editing={editingId === p.id}
            onEdit={() => { setEditingId(p.id); setAdding(false); }}
            onCancelEdit={() => setEditingId(null)}
            onSave={(patch) => { updatePaint(p.id, patch); setEditingId(null); }}
            onDelete={() => deletePaint(p.id)}
            onToggleActive={() => updatePaint(p.id, { active: !(p.active !== false) })}
            onToggleSelect={() => onToggleSelect(p.id)}
            onPick={() => onPickColor(p.hex)}
          />
        ))}
        {filtered.length === 0 && (
          <div className="empty">No paints match this filter.</div>
        )}
      </div>
    </div>
  );
}

function PaintRow({
  paint, selected, editing,
  onEdit, onCancelEdit, onSave, onDelete,
  onToggleActive, onToggleSelect, onPick,
}) {
  if (editing) {
    return (
      <PaintEditor initial={paint} onSave={onSave} onCancel={onCancelEdit} />
    );
  }
  const isActive = paint.active !== false;
  return (
    <div className={`paint-row ${isActive ? '' : 'inactive'} ${selected ? 'selected' : ''}`}>
      <button
        type="button"
        className="paint-swatch"
        style={{ background: paint.hex }}
        title="Use as target"
        onClick={onPick}
      />
      <div className="paint-main">
        <div className="paint-name-row">
          <span className="paint-name">{paint.name}</span>
          <TypeChip type={paint.type} />
        </div>
        <div className="paint-meta">
          <span className="mono dim">{paint.hex.toUpperCase()}</span>
          {paint.notes && <span className="paint-notes"> · {paint.notes}</span>}
        </div>
      </div>
      <div className="paint-actions">
        <button
          type="button"
          className={`pill ${isActive ? 'on' : 'off'}`}
          onClick={onToggleActive}
          title={isActive ? 'Deactivate' : 'Activate'}
        >
          {isActive ? 'Active' : 'Off'}
        </button>
        <IconButton title="Edit" onClick={onEdit}>✎</IconButton>
        <DeleteButton onConfirm={onDelete} />
      </div>
    </div>
  );
}

function PaintEditor({ initial, onSave, onCancel }) {
  const [name, setName] = React.useState(initial.name);
  const [hex, setHex] = React.useState(initial.hex);
  const [type, setType] = React.useState(initial.type);
  const [notes, setNotes] = React.useState(initial.notes || '');
  const [active, setActive] = React.useState(initial.active !== false);
  const valid = name.trim().length > 0 && /^#?[0-9a-f]{6}$/i.test(hex.replace('#', ''));

  const save = () => {
    if (!valid) return;
    const h = hex.startsWith('#') ? hex : '#' + hex;
    onSave({ name: name.trim(), hex: h.toUpperCase(), type, notes: notes.trim(), active });
  };

  return (
    <div className="paint-editor">
      <div className="editor-top">
        <input
          type="color"
          className="editor-color"
          value={/^#?[0-9a-f]{6}$/i.test(hex.replace('#', '')) ? (hex.startsWith('#') ? hex : '#' + hex) : '#888888'}
          onChange={(e) => setHex(e.target.value.toUpperCase())}
        />
        <div className="editor-fields">
          <input
            className="input"
            placeholder="Paint name (e.g. Titanium White)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <div className="editor-row">
            <input
              className="input mono"
              placeholder="#RRGGBB"
              value={hex}
              onChange={(e) => setHex(e.target.value)}
              style={{ width: 110 }}
            />
            <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
              {PAINT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <input
        className="input"
        placeholder="Notes (optional) — e.g. warm undertone, thins well"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <div className="editor-actions">
        <label className="checkbox">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <span>Active (include in mixing)</span>
        </label>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" disabled={!valid} onClick={save}>Save</button>
      </div>
    </div>
  );
}

Object.assign(window, { PaintLibrary, PaintRow, PaintEditor });


// ── target.jsx ─────────────────────────────────────────
// target.jsx — Target color input panel

function TargetPanel({ target, setTarget }) {
  const [mode, setMode] = React.useState('hex');
  const [liveColor, setLiveColor] = React.useState(target);
  const [hexDraft, setHexDraft] = React.useState(target);
  const colorInputRef = React.useRef(null);
  const rgb = hexToRgb(target) || { r: 0, g: 0, b: 0 };

  // Keep live preview and hex draft in sync when target changes from outside
  // (e.g. clicking a paint swatch or committing a different mode).
  React.useEffect(() => { setLiveColor(target); setHexDraft(target); }, [target]);

  // Commit to target only when the color picker is closed/released
  React.useEffect(() => {
    const el = colorInputRef.current;
    if (!el) return;
    const onCommit = (e) => setTarget(e.target.value.toUpperCase());
    el.addEventListener('change', onCommit);
    return () => el.removeEventListener('change', onCommit);
  }, []);

  const commitHexDraft = () => {
    let h = hexDraft.trim();
    if (!h.startsWith('#')) h = '#' + h;
    h = h.toUpperCase();
    if (/^#[0-9A-F]{6}$/.test(h)) {
      setTarget(h);
    } else {
      // Invalid — snap the draft back to the current target
      setHexDraft(target);
    }
  };

  const setFromRgb = (k, v) => {
    const next = { ...rgb, [k]: Math.max(0, Math.min(255, Number(v) || 0)) };
    setTarget(rgbToHex(next));
  };

  const parseRgbString = (s) => {
    const m = s.match(/(\d+)\s*[,\s]\s*(\d+)\s*[,\s]\s*(\d+)/);
    if (!m) return;
    setTarget(rgbToHex({ r: +m[1], g: +m[2], b: +m[3] }));
  };

  return (
    <div className="target">
<h2 className="panel-title">Reference color</h2>

      <div className="target-hero">
        <div
          className="target-swatch"
          style={{ background: liveColor }}
        >
          <input
            ref={colorInputRef}
            type="color"
            value={liveColor}
            onChange={(e) => setLiveColor(e.target.value.toUpperCase())}
            className="target-color-input"
            aria-label="Pick color"
          />
          <span className="pick-hint" style={{ color: readableOn(liveColor) }}>
            click to pick ↗
          </span>
        </div>
        <div className="target-meta">
          <div className="target-big mono">{target.toUpperCase()}</div>
          <div className="target-sub mono dim">
            rgb({rgb.r}, {rgb.g}, {rgb.b})
          </div>
          <div className="target-lab mono dim">
            {(() => {
              const lab = rgbToLab(rgb);
              return `L* ${lab.L.toFixed(0)}  a* ${lab.a.toFixed(0)}  b* ${lab.b.toFixed(0)}`;
            })()}
          </div>
        </div>
      </div>

      <div className="input-modes">
        <Segmented
          value={mode}
          onChange={setMode}
          options={[
            { value: 'hex', label: 'HEX' },
            { value: 'rgb', label: 'RGB' },
            { value: 'paste', label: 'Paste' },
          ]}
        />
      </div>

      {mode === 'hex' && (
        <div className="field-row">
          <label className="mono tiny">HEX</label>
          <input
            className="input mono"
            value={hexDraft}
            onChange={(e) => setHexDraft(e.target.value)}
            onBlur={commitHexDraft}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { commitHexDraft(); e.currentTarget.blur(); }
              if (e.key === 'Escape') { setHexDraft(target); e.currentTarget.blur(); }
            }}
            spellCheck="false"
            autoCapitalize="characters"
            inputMode="text"
          />
        </div>
      )}

      {mode === 'rgb' && (
        <div className="rgb-inputs">
          {['r', 'g', 'b'].map((k) => (
            <div key={k} className="field-row">
              <label className="mono tiny">{k.toUpperCase()}</label>
              <input
                className="input mono"
                type="number"
                min="0"
                max="255"
                value={rgb[k]}
                onChange={(e) => setFromRgb(k, e.target.value)}
              />
            </div>
          ))}
        </div>
      )}

      {mode === 'paste' && (
        <div className="field-row">
          <label className="mono tiny">Paste</label>
          <input
            className="input mono"
            placeholder="rgb(138, 111, 77) or 138, 111, 77"
            onChange={(e) => parseRgbString(e.target.value)}
          />
        </div>
      )}

    </div>
  );
}

Object.assign(window, { TargetPanel });


// ── suggestion.jsx ─────────────────────────────────────────
// suggestion.jsx — Mix suggestion panel

function MixPanel({ target, paints, mode, setMode, unit, setUnit, maxComponents, setMaxComponents }) {
  // Deferred target lets React render UI updates (taps, typing) at higher
  // priority than the expensive recipe solve, so the page stays responsive
  // while the solver catches up in the background.
  const solveTarget = React.useDeferredValue(target);
  const result = React.useMemo(
    () => mixPaints(solveTarget, paints, { mode, maxComponents }),
    [solveTarget, paints, mode, maxComponents]
  );

  if (!result) {
    return (
      <div className="mix-panel">
        <div className="eyebrow">03 · Recipe</div>
        <h2 className="panel-title">Mix suggestion</h2>
        <div className="empty big">
          Activate at least one paint in your palette to get a mix suggestion.
        </div>
      </div>
    );
  }

  const { components, predHex, dE, target: tRgb } = result;
  const parts = weightsToParts(components.map((c) => c.weight));
  const simpleParts = simplifyParts(parts);
  const totalParts = simpleParts.reduce((a, b) => a + b, 0);
  const totalPct = components.reduce((a, c) => a + c.weight, 0);
  const tone = deltaELabel(dE).tone;

  return (
    <div className="mix-panel">
      <div className="mix-head">
        <div>
<h2 className="panel-title">Mix suggestion</h2>
        </div>
        <DeltaBadge dE={dE} />
      </div>

      <div className="compare">
        <div className="compare-col">
          <div className="compare-label mono tiny">TARGET</div>
          <div className="compare-swatch" style={{ background: target }} />
          <div className="compare-hex mono">{target.toUpperCase()}</div>
        </div>
        <div className="compare-mid">
          <div className="compare-split">
            <div style={{ background: target, flex: 1 }} />
            <div style={{ background: predHex, flex: 1 }} />
          </div>
          <div className="mono tiny dim" style={{ textAlign: 'center', marginTop: 6 }}>
            side-by-side
          </div>
        </div>
        <div className="compare-col">
          <div className="compare-label mono tiny">PREDICTED</div>
          <div className="compare-swatch" style={{ background: predHex }} />
          <div className="compare-hex mono">{predHex.toUpperCase()}</div>
        </div>
      </div>

      <div className="recipe-controls">
        <div className="rc-group">
          <label className="tiny dim">ALGORITHM</label>
          <Segmented
            value={mode}
            onChange={setMode}
            options={[
              { value: 'rgb', label: 'RGB match' },
              { value: 'pigment', label: 'Pigment-aware' },
            ]}
          />
        </div>
        <div className="rc-group">
          <label className="tiny dim">UNIT</label>
          <Segmented
            value={unit}
            onChange={setUnit}
            options={[
              { value: 'parts', label: 'Parts' },
              { value: 'pct', label: '%' },
              { value: 'drops', label: 'Drops' },
            ]}
          />
        </div>
        <div className="rc-group">
          <label className="tiny dim">MAX PAINTS</label>
          <div className="stepper">
            <button onClick={() => setMaxComponents(Math.max(2, maxComponents - 1))}>−</button>
            <span className="mono">{maxComponents}</span>
            <button onClick={() => setMaxComponents(Math.min(6, maxComponents + 1))}>+</button>
          </div>
        </div>
      </div>

      <div className="recipe">
        {components.map((c, i) => {
          const pct = (c.weight / totalPct) * 100;
          const part = simpleParts[i] || 1;
          const drops = Math.max(1, Math.round(c.weight * 40));
          const display =
            unit === 'parts' ? `${part}${part === 1 ? ' part' : ' parts'}` :
            unit === 'pct'   ? `${pct.toFixed(1)}%` :
                               `${drops}${drops === 1 ? ' drop' : ' drops'}`;
          return (
            <div key={c.paint.id} className="recipe-row">
              <div
                className="recipe-swatch"
                style={{ background: c.paint.hex, color: readableOn(c.paint.hex) }}
              >
                <span className="mono">{i + 1}</span>
              </div>
              <div className="recipe-main">
                <div className="recipe-name">{c.paint.name}</div>
                <div className="recipe-sub mono dim">
                  {c.paint.hex.toUpperCase()} · <TypeChipInline type={c.paint.type} />
                </div>
              </div>
              <div className="recipe-bar-wrap">
                <div
                  className="recipe-bar"
                  style={{
                    width: `${Math.max(4, pct)}%`,
                    background: c.paint.hex,
                  }}
                />
              </div>
              <div className="recipe-qty mono">{display}</div>
            </div>
          );
        })}
      </div>

      <div className="recipe-foot">
        <div className="tiny dim">
          {unit === 'parts' && `Total: ${totalParts} ${totalParts === 1 ? 'part' : 'parts'}`}
          {unit === 'pct' && 'Weights sum to 100%'}
          {unit === 'drops' && `Total: ~${components.reduce((a, c) => a + Math.max(1, Math.round(c.weight * 40)), 0)} drops (≈ ${Math.round(components.reduce((a, c) => a + Math.max(1, Math.round(c.weight * 40)), 0) * 0.05 * 10) / 10} ml @ 0.05 ml/drop)`}
        </div>
        <div className={`tone tone-${tone}`}>
          {tone === 'ok' && 'This should be a convincing match for hobby/airbrush work.'}
          {tone === 'warn' && 'Usable but not exact — add more primaries or adjust by eye.'}
          {tone === 'bad' && 'Your palette can\'t reach this color well. Consider adding missing primaries.'}
        </div>
      </div>
    </div>
  );
}

// Tiny inline type chip for recipe rows
function TypeChipInline({ type }) {
  const meta = PAINT_TYPES.find((t) => t.value === type);
  return <span className={`chip-inline chip-${type}`}>{meta ? meta.label : type}</span>;
}

Object.assign(window, { MixPanel });


// ── app.jsx ─────────────────────────────────────────
// app.jsx — main PaintMix app

// ── JSONBin config ─────────────────────────────────────────────────────────
// 1. Sign up free at https://jsonbin.io
// 2. Account → API Keys → copy your Master Key
// 3. "+ Create Bin" → paste {} → name it paintmix → save
// 4. Copy the Bin ID from the bin URL
const JSONBIN_KEY    = '$2a$10$SB9LmyATKBrbopGWPNqd2.Z/S9dNGmK1h4dt8ElsL6xw4ZMsp3TDi';
const JSONBIN_BIN_ID = '69eb528136566621a8e88e57';
// ──────────────────────────────────────────────────────────────────────────

const SAVE_DELAY = 1000;
const JSONBIN_URL = `https://api.jsonbin.io/v3/b/${JSONBIN_BIN_ID}`;
const CACHE_KEY   = 'paintmix:cache';

function loadCache() {
  try { const r = localStorage.getItem(CACHE_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
function writeCache(state) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(state)); } catch {}
}

async function fetchPalette() {
  if (JSONBIN_BIN_ID === 'YOUR_BIN_ID') return null;
  try {
    const res = await fetch(`${JSONBIN_URL}/latest`, {
      headers: { 'X-Master-Key': JSONBIN_KEY },
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json.record || null;
  } catch { return null; }
}

async function savePalette(state) {
  writeCache(state);
  if (JSONBIN_BIN_ID === 'YOUR_BIN_ID') return;
  try {
    await fetch(JSONBIN_URL, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-Master-Key': JSONBIN_KEY },
      body: JSON.stringify(state),
    });
  } catch {}
}

function parseImportedPaints(text) {
  const raw = JSON.parse(text.trim());
  const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.paints) ? raw.paints : null;
  if (!arr) throw new Error('Expected an array or {"paints":[…]}');
  return arr.map((p) => {
    if (!p.name || !p.hex) throw new Error(`Paint missing name or hex: ${JSON.stringify(p)}`);
    const hex = (p.hex.startsWith('#') ? p.hex : '#' + p.hex).toUpperCase();
    if (!/^#[0-9A-F]{6}$/.test(hex)) throw new Error(`Invalid hex: ${p.hex}`);
    return {
      id:     'p' + Math.random().toString(36).slice(2, 8),
      name:   String(p.name).trim(),
      hex,
      type:   p.type   || 'primary',
      notes:  p.notes  || '',
      active: p.active !== false,
    };
  });
}

function ImportModal({ onImport, onClose }) {
  const [text, setText]   = React.useState('');
  const [error, setError] = React.useState('');

  const tryImport = () => {
    try {
      const paints = parseImportedPaints(text);
      onImport(paints);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,.45)', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'var(--paper)', border: '.5px solid var(--rule)',
        borderRadius: 'var(--radius)', padding: '20px 24px',
        width: 480, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 12,
        boxShadow: '0 8px 40px rgba(0,0,0,.25)',
      }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>Import paints</div>
        <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
          Paste a JSON array <code style={{ fontFamily: 'monospace' }}>[{'{'}…{'}'}]</code> or a palette export <code style={{ fontFamily: 'monospace' }}>{'{\"paints\":[…]}'}</code>.
          Imported paints are appended to your current palette.
        </div>
        <textarea
          autoFocus
          value={text}
          onChange={(e) => { setText(e.target.value); setError(''); }}
          placeholder={'[\n  { "name": "Ivory", "hex": "#FFFFF0", "type": "white" },\n  …\n]'}
          style={{
            fontFamily: 'monospace', fontSize: 12,
            height: 200, resize: 'vertical',
            padding: '8px 10px', borderRadius: 6,
            border: '.5px solid var(--rule)',
            background: 'var(--bg)', color: 'var(--ink)',
            outline: 'none', width: '100%', boxSizing: 'border-box',
          }}
        />
        {error && (
          <div style={{ fontSize: 12, color: '#c0392b', fontFamily: 'monospace' }}>{error}</div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!text.trim()} onClick={tryImport}>
            Import
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [ready, setReady]               = React.useState(false);
  const [importing, setImporting]       = React.useState(false);
  const [paints, setPaints]             = React.useState(SEED_PAINTS);
  const [target, setTarget]             = React.useState('#8A6F4D');
  const [mode, setMode]                 = React.useState('pigment');
  const [unit, setUnit]                 = React.useState('parts');
  const [maxComponents, setMaxComponents] = React.useState(4);
  const [selectedIds]                   = React.useState(new Set());
  const saveTimer                       = React.useRef(null);

  React.useEffect(() => {
    const apply = (data) => {
      if (data.paints)        setPaints(data.paints);
      if (data.target)        setTarget(data.target);
      if (data.mode)          setMode(data.mode);
      if (data.unit)          setUnit(data.unit);
      if (data.maxComponents) setMaxComponents(data.maxComponents);
    };

    const cached = loadCache();
    if (cached) {
      apply(cached);
      setReady(true);
      // background sync — update if server has newer data (e.g. from another device)
      fetchPalette().then((data) => {
        if (data && JSON.stringify(data) !== JSON.stringify(cached)) {
          apply(data);
          writeCache(data);
        }
      });
    } else {
      fetchPalette().then((data) => {
        if (data) { apply(data); writeCache(data); }
        setReady(true);
      });
    }
  }, []);

  React.useEffect(() => {
    if (!ready) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      savePalette({ paints, target, mode, unit, maxComponents });
    }, SAVE_DELAY);
    return () => clearTimeout(saveTimer.current);
  }, [paints, target, mode, unit, maxComponents, ready]);

  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);

  // Derived theme styles from tweaks
  const themeStyle = {
    '--bg': t.theme === 'dark'  ? '#14120e' : t.theme === 'cool' ? '#eff1f4' : '#f6f2ea',
    '--ink': t.theme === 'dark' ? '#eae4d4' : '#1e1b16',
    '--ink-soft': t.theme === 'dark' ? 'rgba(234,228,212,.62)' : 'rgba(30,27,22,.62)',
    '--ink-dim': t.theme === 'dark' ? 'rgba(234,228,212,.38)' : 'rgba(30,27,22,.38)',
    '--paper': t.theme === 'dark' ? '#1c1915' : t.theme === 'cool' ? '#ffffff' : '#fbf8f2',
    '--rule': t.theme === 'dark' ? 'rgba(234,228,212,.12)' : 'rgba(30,27,22,.12)',
    '--accent': t.accent,
    '--radius': `${t.radius}px`,
    '--display-font': t.display === 'serif'
      ? `'Newsreader', 'Iowan Old Style', Georgia, serif`
      : t.display === 'grotesk'
      ? `'Space Grotesk', system-ui, sans-serif`
      : `'IBM Plex Mono', ui-monospace, monospace`,
  };

  const onToggleSelect = () => {};
  const onPickColor = (hex) => setTarget(hex);

  if (!ready) return null;

  return (
    <div className="app" style={themeStyle} data-theme={t.theme} data-density={t.density}>
<main className="grid">
        <section className="col col-left">
          <TargetPanel target={target} setTarget={setTarget} />
        </section>

        <section className="col col-mid">
          <MixPanel
            target={target}
            paints={paints}
            mode={mode}
            setMode={setMode}
            unit={unit}
            setUnit={setUnit}
            maxComponents={maxComponents}
            setMaxComponents={setMaxComponents}
          />
        </section>

        <section className="col col-right">
          <PaintLibrary
            paints={paints}
            setPaints={setPaints}
            selectedIds={selectedIds}
            onToggleSelect={onToggleSelect}
            onPickColor={onPickColor}
          />
        </section>
      </main>

      <footer className="bottombar mono tiny dim">
        <span>Palette stored on server · {paints.length} paints · {paints.filter((p) => p.active !== false).length} active</span>
        <span style={{ flex: 1 }} />
        <button
          className="link-btn"
          onClick={() => setImporting(true)}
        >import JSON</button>
        <span> · </span>
        <button
          className="link-btn"
          onClick={() => {
            const data = JSON.stringify({ paints, target }, null, 2);
            navigator.clipboard?.writeText(data);
          }}
        >copy palette JSON</button>
        <span> · </span>
        <button
          className="link-btn"
          onClick={() => {
            if (confirm('Reset palette to factory seed?')) setPaints(SEED_PAINTS);
          }}
        >reset seed</button>
      </footer>

      {importing && (
        <ImportModal
          onImport={(incoming) => {
            setPaints((ps) => [...ps, ...incoming]);
            setImporting(false);
          }}
          onClose={() => setImporting(false)}
        />
      )}

      <TweaksPanel title="Tweaks">
        <TweakSection label="Theme" />
        <TweakRadio
          label="Palette"
          value={t.theme}
          options={[
            { value: 'warm', label: 'Warm' },
            { value: 'cool', label: 'Cool' },
            { value: 'dark', label: 'Dark' },
          ]}
          onChange={(v) => setTweak('theme', v)}
        />
        <TweakColor label="Accent" value={t.accent} onChange={(v) => setTweak('accent', v)} />
        <TweakSection label="Type" />
        <TweakRadio
          label="Display"
          value={t.display}
          options={[
            { value: 'serif', label: 'Serif' },
            { value: 'grotesk', label: 'Grotesk' },
            { value: 'mono', label: 'Mono' },
          ]}
          onChange={(v) => setTweak('display', v)}
        />
        <TweakSection label="Layout" />
        <TweakRadio
          label="Density"
          value={t.density}
          options={['compact', 'regular', 'comfy']}
          onChange={(v) => setTweak('density', v)}
        />
        <TweakSlider
          label="Radius"
          value={t.radius}
          min={0} max={20} step={1} unit="px"
          onChange={(v) => setTweak('radius', v)}
        />
      </TweaksPanel>
    </div>
  );
}

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "theme": "warm",
  "accent": "#C4246C",
  "display": "serif",
  "density": "regular",
  "radius": 10
}/*EDITMODE-END*/;

ReactDOM.createRoot(document.getElementById('root')).render(<App />);
